import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FaUser, FaLock, FaSpinner, FaEnvelope } from 'react-icons/fa';
import { FiUserPlus } from 'react-icons/fi';

export default function Register() {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { register } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (password !== confirmPassword) {
            return setError('Passwords do not match');
        }

        try {
            setError('');
            setLoading(true);
            await register(email, password, name);
            navigate('/dashboard');
        } catch (err) {
            setError('Failed to create an account: ' + err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen relative flex items-center justify-center overflow-hidden bg-blue-50">
            {/* Background Decorations (Cloud/Sky effect) */}
            <div className="absolute inset-0 bg-gradient-to-tr from-blue-200 via-blue-100 to-white"></div>
            <div className="absolute -top-20 -left-20 w-96 h-96 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
            <div className="absolute -bottom-20 -right-20 w-96 h-96 bg-purple-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>

            {/* Register Card */}
            <div className="relative w-full max-w-md p-8 bg-white/60 backdrop-blur-2xl rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-white/50 m-4">

                {/* Logo/Icon */}
                <div className="flex justify-center mb-6">
                    <div className="w-14 h-14 bg-white rounded-2xl shadow-lg flex items-center justify-center text-gray-800">
                        {/* Using the icon as per consistent design request, user can swap for img if preferred like in Login */}
                        <FiUserPlus size={26} />
                    </div>
                </div>

                {/* Header */}
                <div className="text-center mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Create an account</h2>
                    <p className="mt-2 text-sm text-gray-500 font-medium">
                        Join Sudesh Maths and start learning today.
                    </p>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="mb-6 p-4 bg-red-50/80 border border-red-100 rounded-2xl text-red-600 text-sm font-medium text-center">
                        {error}
                    </div>
                )}

                {/* Form */}
                <form className="space-y-4" onSubmit={handleSubmit}>
                    <div className="space-y-1">
                        <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                <FaUser className="h-4 w-4 text-gray-400 group-focus-within:text-gray-600 transition-colors" />
                            </div>
                            <input
                                id="name"
                                type="text"
                                required
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="block w-full pl-11 pr-4 py-3.5 bg-gray-50/50 border border-transparent rounded-2xl text-sm font-medium text-gray-900 placeholder-gray-400 focus:outline-none focus:bg-white focus:ring-4 focus:ring-gray-100/50 focus:border-gray-200 transition-all duration-200"
                                placeholder="Full Name"
                            />
                        </div>
                    </div>

                    <div className="space-y-1">
                        <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                <FaEnvelope className="h-4 w-4 text-gray-400 group-focus-within:text-gray-600 transition-colors" />
                            </div>
                            <input
                                id="email"
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="block w-full pl-11 pr-4 py-3.5 bg-gray-50/50 border border-transparent rounded-2xl text-sm font-medium text-gray-900 placeholder-gray-400 focus:outline-none focus:bg-white focus:ring-4 focus:ring-gray-100/50 focus:border-gray-200 transition-all duration-200"
                                placeholder="Email Address"
                            />
                        </div>
                    </div>

                    <div className="space-y-1">
                        <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                <FaLock className="h-4 w-4 text-gray-400 group-focus-within:text-gray-600 transition-colors" />
                            </div>
                            <input
                                id="password"
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="block w-full pl-11 pr-4 py-3.5 bg-gray-50/50 border border-transparent rounded-2xl text-sm font-medium text-gray-900 placeholder-gray-400 focus:outline-none focus:bg-white focus:ring-4 focus:ring-gray-100/50 focus:border-gray-200 transition-all duration-200"
                                placeholder="Password"
                            />
                        </div>
                    </div>

                    <div className="space-y-1">
                        <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                <FaLock className="h-4 w-4 text-gray-400 group-focus-within:text-gray-600 transition-colors" />
                            </div>
                            <input
                                id="confirmPassword"
                                type="password"
                                required
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="block w-full pl-11 pr-4 py-3.5 bg-gray-50/50 border border-transparent rounded-2xl text-sm font-medium text-gray-900 placeholder-gray-400 focus:outline-none focus:bg-white focus:ring-4 focus:ring-gray-100/50 focus:border-gray-200 transition-all duration-200"
                                placeholder="Confirm Password"
                            />
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full h-12 flex items-center justify-center bg-gray-900 hover:bg-gray-800 text-white rounded-2xl font-bold transition-all duration-200 transform active:scale-95 shadow-lg shadow-gray-900/20 mt-4"
                    >
                        {loading ? <FaSpinner className="animate-spin h-5 w-5" /> : 'Create Account'}
                    </button>
                </form>

                
              

                {/* Sign In Link */}
                <p className="mt-8 text-center text-sm text-gray-500 font-medium">
                    Already have an account?{' '}
                    <Link to="/login" className="text-gray-900 font-bold hover:underline">
                        Sign in
                    </Link>
                </p>
            </div>
        </div>
    );
}
